import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';

interface TiltCardProps {
  children: React.ReactNode;
  className?: string;
  maxTilt?: number;
  scale?: number;
}

export const TiltCard: React.FC<TiltCardProps> = ({
  children,
  className,
  maxTilt = 6,
  scale = 1.012,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const hovered = useMotionValue(0);

  const springX = useSpring(x, { stiffness: 180, damping: 18, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 180, damping: 18, mass: 0.4 });
  const springScale = useSpring(hovered, { stiffness: 180, damping: 18, mass: 0.4 });

  const rotateX = useTransform(springY, [-0.5, 0.5], [maxTilt, -maxTilt]);
  const rotateY = useTransform(springX, [-0.5, 0.5], [-maxTilt, maxTilt]);
  const s = useTransform(springScale, [0, 1], [1, scale]);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    x.set(px / rect.width - 0.5);
    y.set(py / rect.height - 0.5);
    el.style.setProperty('--px', `${px}px`);
    el.style.setProperty('--py', `${py}px`);
  };

  const handleEnter = () => {
    if (!reduce) hovered.set(1);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
    hovered.set(0);
  };

  if (reduce) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className="tilt-perspective" style={{ height: '100%' }}>
      <motion.div
        ref={ref}
        className={`tilt-card ${className ?? ''}`.trim()}
        style={{ rotateX, rotateY, scale: s, transformStyle: 'preserve-3d', height: '100%' }}
        onMouseMove={handleMove}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {children}
        <span className="specular" />
      </motion.div>
    </div>
  );
};
